import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Package, TrendingUp, Calculator, Layers } from "lucide-react";

const COLORS = ['#1B4332', '#2D6A4F', '#40916C', '#52B788', '#74C69D', '#95D5B2', '#3b82f6', '#6366f1'];

const categoriasLabels = {
  semente: "Sementes",
  fertilizante: "Fertilizantes",
  defensivo: "Defensivos",
  combustivel: "Combustível",
  racao: "Ração",
  medicamento: "Medicamentos",
  outro: "Outros"
};

export default function EstoqueValorizacao({ insumos }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Valor total e por categoria
  const valorizacao = useMemo(() => {
    const porCategoria = {};
    let valorTotal = 0;
    let qtdItens = 0;

    insumos.forEach(i => {
      const valor = (i.quantidade_estoque || 0) * (i.custo_unitario || 0);
      valorTotal += valor;
      qtdItens++;

      const cat = i.categoria || 'outro';
      if (!porCategoria[cat]) {
        porCategoria[cat] = { valor: 0, qtd: 0, itens: [] };
      }
      porCategoria[cat].valor += valor;
      porCategoria[cat].qtd += i.quantidade_estoque || 0;
      porCategoria[cat].itens.push(i);
    });

    const categorias = Object.entries(porCategoria)
      .map(([key, data]) => ({
        categoria: key,
        label: categoriasLabels[key] || key,
        ...data
      }))
      .sort((a, b) => b.valor - a.valor);

    return { valorTotal, qtdItens, categorias };
  }, [insumos]);

  // Top 5 itens por valor
  const topItens = useMemo(() => {
    return insumos
      .map(i => ({
        ...i,
        valorTotal: (i.quantidade_estoque || 0) * (i.custo_unitario || 0)
      }))
      .sort((a, b) => b.valorTotal - a.valorTotal)
      .slice(0, 5);
  }, [insumos]);

  // Custo médio por categoria
  const custoMedioPorCategoria = useMemo(() => {
    return valorizacao.categorias.map(cat => {
      const custoMedio = cat.itens.length > 0
        ? cat.itens.reduce((a, i) => a + (i.custo_unitario || 0), 0) / cat.itens.length
        : 0;
      return {
        categoria: cat.label,
        custoMedio,
        qtdItens: cat.itens.length
      };
    });
  }, [valorizacao]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-medium text-stone-800">{payload[0].payload.label || payload[0].payload.categoria}</p>
          <p className="text-sm text-stone-600">Valor: {formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cards Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4" />
              <p className="text-blue-100 text-xs">Total em Estoque</p>
            </div>
            <p className="text-xl font-bold">{formatCurrency(valorizacao.valorTotal)}</p>
            <p className="text-xs text-blue-200 mt-1">{valorizacao.qtdItens} itens</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calculator className="w-4 h-4" />
              <p className="text-green-100 text-xs">Custo Médio/Item</p>
            </div>
            <p className="text-xl font-bold">
              {formatCurrency(valorizacao.qtdItens > 0 ? valorizacao.valorTotal / valorizacao.qtdItens : 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4" />
              <p className="text-purple-100 text-xs">Categorias</p>
            </div>
            <p className="text-xl font-bold">{valorizacao.categorias.length}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4" />
              <p className="text-amber-100 text-xs">Maior Categoria</p>
            </div>
            <p className="text-lg font-bold truncate">
              {valorizacao.categorias[0]?.label || '-'}
            </p>
            <p className="text-xs text-amber-200">{formatCurrency(valorizacao.categorias[0]?.valor)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico Pizza - Valor por Categoria */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-blue-600" />
              Valor por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            {valorizacao.categorias.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-stone-500">
                Sem dados
              </div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={valorizacao.categorias}
                      dataKey="valor"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ label, percent }) => `${label.substring(0, 8)} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {valorizacao.categorias.map((entry, index) => (
                        <Cell key={entry.categoria} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
              {valorizacao.categorias.map((cat, index) => (
                <div key={cat.categoria} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span className="text-stone-600">{cat.label}</span>
                    <Badge variant="secondary" className="text-xs">{cat.itens.length}</Badge>
                  </div>
                  <span className="font-medium">{formatCurrency(cat.valor)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top 5 Itens */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Top 5 Itens por Valor
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topItens.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-stone-500">
                Sem dados
              </div>
            ) : (
              <div className="space-y-3">
                {topItens.map((item, index) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">{item.nome}</p>
                      <p className="text-xs text-stone-500">
                        {item.quantidade_estoque} {item.unidade} × {formatCurrency(item.custo_unitario)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-stone-800">{formatCurrency(item.valorTotal)}</p>
                      <p className="text-xs text-stone-500">
                        {valorizacao.valorTotal > 0 ? ((item.valorTotal / valorizacao.valorTotal) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custo Médio por Categoria */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="w-5 h-5 text-purple-600" />
            Custo Médio por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={custoMedioPorCategoria} margin={{ top: 10, right: 30, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="categoria" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                <YAxis 
                  tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact', style: 'currency', currency: 'BRL' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="custoMedio" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Custo Médio" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}